import {
  Container,
  Stack,
  Box,
  Text,
  HStack,
  Divider,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Flex,
  Badge,
  Image,
  Wrap,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import t from "../translations";

function ExperienceCard({ exp, color, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -3 }}
      style={{ borderRadius: "var(--chakra-radii-xl)" }}
    >
      <Card
        size="sm"
        borderRadius="xl"
        _hover={{ boxShadow: "xl" }}
        transition="box-shadow 0.25s ease"
      >
        <CardHeader>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
          >
            <HStack spacing={3}>
              <Image
                src={exp.image}
                h={10}
                w={10}
                objectFit="contain"
                alt={`${exp.company} logo`}
                borderRadius="md"
              />
              <Box>
                <Text fontWeight={600}>{exp.company}</Text>
                <Text fontSize="sm" color="gray.500">
                  {exp.position}
                </Text>
              </Box>
            </HStack>
            <Text
              fontWeight={400}
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.500"
              whiteSpace="nowrap"
            >
              {exp.duration}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <Stack spacing={2}>
            {exp.listItems.map((item, i) => (
              <Flex key={i} fontSize="sm" align="flex-start" gap={2}>
                <ChevronRightIcon color={`${color}.400`} mt="4px" flexShrink={0} />
                <Text>{item}</Text>
              </Flex>
            ))}
          </Stack>
        </CardBody>
        <CardFooter pt={0}>
          <Wrap spacing={2}>
            {exp.badges.map((badge) => (
              <Badge
                key={badge.name}
                colorScheme={badge.colorScheme}
                fontSize="xs"
                borderRadius="full"
                px={2}
                py={0.5}
              >
                {badge.name}
              </Badge>
            ))}
          </Wrap>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export default function Experience({ color }) {
  return (
    <Container maxW="4xl" id="experience" py={{ base: 16, md: 24 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              {t.experience.heading}
            </Text>
          </HStack>
          <Divider />
        </Stack>
        <Stack px={4} spacing={6}>
          {t.experience.data.map((exp, i) => (
            <ExperienceCard
              key={exp.company}
              exp={exp}
              color={color}
              index={i}
            />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}
